import React from 'react';
import { Plus } from 'lucide-react';
import { Handle, useNodeId, useStore, type Position } from '@xyflow/react';
import { cn } from '@cotera/client/v0/lib/utils';
import { useWorkflowAddNodeFromSource } from './workflow-add-node-context';

export type WorkflowHandleProps = {
  readonly type: 'source' | 'target';
  readonly position: Position;
};

/**
 * The resting look for every port: an open rounded square straddling the node's
 * edge, echoing the card's own `rounded-2xl` corners.
 *
 * The radius can't literally be the card's 16px — on a 14px port that is past
 * half the width, which renders as a circle. 4px (a bit under a third of the
 * width) is the proportional equivalent, so it reads as the same family of shape.
 *
 * No offset overrides: React Flow centres handles on the edge with a transform,
 * which is the alignment we want, and that transform must stay untouched.
 */
const PORT_SHAPE =
  'size-3.5! rounded-[4px]! border-2! border-muted-foreground/50! bg-card! transition-colors';

/**
 * A node's connection port.
 *
 * React Flow's default handle is a 6px filled dot in the library's own ink
 * colour — it reads as a bullet or a shadow artefact, and nothing about it
 * suggests you can drag from it. Worse, the canvas already supports "drag off a
 * source and drop on empty pane to add the next step" (see `resolveConnectEnd`)
 * and that behaviour was entirely undiscoverable.
 *
 * So every port rests as an open rounded square, and once its node is *selected*
 * both ports fill with primary and reveal a "+": the output adds the next step,
 * the input adds the step before. Connecting doesn't change either one, since the
 * edge itself then carries that information.
 *
 * Tying the reveal to selection rather than hover means the click target only
 * exists while it is visible — on an unselected card the ports stay plain
 * handles, so a click there still does what React Flow always did.
 *
 * On a canvas that cannot be connected on at all, none of that applies and the
 * port renders invisibly — see the guard below.
 *
 * Only renders inside a React Flow custom node — `useStore` needs the provider.
 */
export const WorkflowHandle: React.FC<WorkflowHandleProps> = ({
  type,
  position,
}) => {
  const nodeId = useNodeId();
  const addFromSource = useWorkflowAddNodeFromSource();
  // `nodesConnectable` is the canvas's own edit gate (`mutations.canEdit` in the
  // editor, hard `false` on every read-only surface), so the port stays in step
  // with whether editing is allowed at all.
  const connectable = useStore((state) => state.nodesConnectable);
  const selected = useStore((state) =>
    nodeId === null ? false : state.nodeLookup.get(nodeId)?.selected === true
  );

  // A port exists to advertise "drag from here to connect this up". A canvas that
  // cannot be connected on has nothing to advertise, so the read-only surfaces —
  // the run graph, the run-workflow drawer's preview, the nested-workflow
  // hierarchy — were drawing ports for an interaction they do not offer, and a
  // "+" that could never fire. The handle itself stays: edges route to it, and
  // React Flow drops an edge whose handle it cannot find.
  if (connectable !== true) {
    return (
      <Handle
        type={type}
        position={position}
        isConnectable={false}
        className="opacity-0"
      />
    );
  }

  // `connectable` is settled by the guard above, so this is only about the node.
  const showAdd = selected && addFromSource !== null && nodeId !== null;

  const label = type === 'source' ? 'Add next step' : 'Add previous step';

  return (
    <Handle
      type={type}
      position={position}
      // Spread conditionally rather than passing `onClick={undefined}`: `...rest`
      // lands after React Flow's own click handler, so even an explicit
      // undefined would clobber its click-to-connect on a resting port.
      {...(showAdd
        ? {
            title: label,
            'aria-label': label,
            onClick: (event: React.MouseEvent) => {
              event.stopPropagation();
              addFromSource(
                nodeId,
                type === 'source' ? 'downstream' : 'upstream'
              );
            },
          }
        : {})}
      className={cn(
        PORT_SHAPE,
        // `leading-none` so the glyph has no stray line-box height to fight
        // against when it is centred in the shape.
        'flex items-center justify-center leading-none text-primary-foreground',
        showAdd && 'border-primary! bg-primary!'
      )}
    >
      {/* Always mounted so the reveal can cross-fade; the port owns the pointer
          interaction, so the glyph must never eat it. */}
      <Plus
        className={cn(
          'pointer-events-none size-2 shrink-0 transition-opacity',
          showAdd ? 'opacity-100' : 'opacity-0'
        )}
      />
    </Handle>
  );
};
